import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { agency } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Champs manquants", description: "Merci de renseigner votre nom, email et message." });
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/contact`, form);
      toast({ title: "Message envoyé — merci !", description: "L'équipe Solès Immo vous répondra dans les plus brefs délais." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast({ title: "Erreur", description: "Une erreur est survenue. Merci de réessayer ou de nous appeler." });
    } finally {
      setLoading(false);
    }
  };

  const infos = [
    { icon: MapPin, label: "Adresse", value: `${agency.address}, ${agency.zip}` },
    { icon: Phone, label: "Téléphone", value: agency.phone, href: `tel:${agency.phoneHref}` },
    { icon: Mail, label: "Email", value: agency.email, href: `mailto:${agency.email}` },
  ];

  return (
    <section id="contact" className="py-24 md:py-32 bg-secondary/50">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="eyebrow-line text-accent tracking-[0.3em] text-xs font-medium uppercase">
            Contact
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-5 text-foreground">
            Parlons de votre projet
          </h2>
          <p className="text-muted-foreground mt-4 text-lg">
            Une question, un projet ? Notre équipe vous répond avec plaisir.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Info + map */}
          <div className="space-y-6">
            <div className="grid sm:grid-cols-1 gap-4">
              {infos.map((it) => (
                <div key={it.label} className="flex items-start gap-4 bg-card rounded-2xl p-5 border border-border">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <it.icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{it.label}</p>
                    {it.href ? (
                      <a href={it.href} className="text-foreground font-medium hover:text-accent transition-colors">{it.value}</a>
                    ) : (
                      <p className="text-foreground font-medium">{it.value}</p>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-4 bg-card rounded-2xl p-5 border border-border">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Clock className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Horaires</p>
                  {agency.hours.map((h) => (
                    <p key={h.day} className="text-foreground/85 text-sm">
                      <span className="font-medium">{h.day}</span> — {h.time}
                    </p>
                  ))}
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-border h-64">
              <iframe
                title="Carte Solès Immo"
                src="https://www.google.com/maps?q=106+route+de+la+Reine+92100+Boulogne-Billancourt&output=embed"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>

          {/* Form */}
          <div className="bg-card rounded-3xl p-8 md:p-10 shadow-lg border border-border">
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-1.5">
                <Label>Nom complet *</Label>
                <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Votre nom" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="vous@email.fr" />
                </div>
                <div className="space-y-1.5">
                  <Label>Téléphone</Label>
                  <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="06 .." />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Votre message *</Label>
                <Textarea rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Décrivez votre projet..." />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full text-base">
                <Send className="h-4 w-4 mr-2" /> Envoyer le message
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
